import { Alert, Button, Card, Space, Typography } from "antd";
import type { Page } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import {
    DateRangePicker as ZhDateRangePicker,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { SHIP_CODE_COLOR, SHIP_INFO_STATE } from "@/meta/logistics/shipInfo";

export interface ShipPortraitQuery {
    scope?: number;
    startDate?: string;
    endDate?: string;
}

export interface ShipPortReportQuery {
    page: number;
    size: number;
    scope?: number;
    startDate?: string;
    endDate?: string;
}

/** 进出港报告字段沿用 Vue 分页接口，允许接口返回空值以便统一展示占位符。 */
export interface ShipPortReport {
    id?: number | string;
    reportId?: number | string;
    index?: number;
    inOutType?: unknown;
    portName?: unknown;
    reportTime?: unknown;
    goodsName?: unknown;
    actualTon?: unknown;
    loadingUnloadingTon?: unknown;
}

export type ShipPortReportPage = Page<ShipPortReport>;

/** 船舶画像接口返回的字段；列表项兼容 Vue 接口返回的字符串和对象。 */
export interface ShipPortraitData {
    creditCode?: number | string;
    shipType?: unknown;
    usualGoodsList?: unknown[];
    usualEntryPorts?: unknown[];
    usualExitPorts?: unknown[];
    referenceTon?: unknown;
    platformVisitCount?: unknown;
    quoteCount?: unknown;
    shippingOrderCount?: unknown;
    emptyShipReportCount?: unknown;
    timelineMonths?: Array<string | Record<string, unknown>> | string;
    platformVisitModuleStats?: ShipPortraitModuleStat[];
    platformBusinessModuleStats?: ShipPortraitModuleStat[];
}

/** 平台模块统计兼容 Vue 接口的数字、字符串和对象月度数据。 */
export interface ShipPortraitModuleStat {
    moduleName?: unknown;
    monthCounts?: ShipPortraitMonthCounts;
}

export type ShipPortraitMonthCount = number | string | Record<string, unknown>;
export type ShipPortraitMonthCounts = ShipPortraitMonthCount[] | string | Record<string, unknown>;

export interface ShipPortraitPanelProps {
    shipId: string;
    shipState?: number | string;
    loadPortrait?: (shipId: string, params: ShipPortraitQuery) => Promise<ShipPortraitData>;
    loadPortReports?: (shipId: string, params: ShipPortReportQuery) => Promise<ShipPortReportPage>;
}

type ShipPortraitDateRange = [string | null, string | null] | null;

/** 复用 Vue 船舶画像接口，默认使用近半年的 scope=5 查询。 */
export async function loadShipPortrait(shipId: string, params: ShipPortraitQuery): Promise<ShipPortraitData> {
    const response = await shippingRequestHttp.service.get<ShipPortraitData>(
        `/ship/profile/${encodeURIComponent(shipId)}`,
        { params }
    );
    return response.data;
}

/** 复用 Vue 进出港报告分页接口，日期范围存在时优先传 startDate/endDate。 */
export async function loadPortReports(shipId: string, params: ShipPortReportQuery): Promise<ShipPortReportPage> {
    const requestParams: Record<string, string | number> = {
        id: shipId,
        page: params.page,
        size: params.size
    };

    // Vue 接口在自定义日期范围下不传 scope，快捷时间范围则始终保留默认 scope=5。
    if (params.startDate && params.endDate) {
        requestParams.startDate = params.startDate;
        requestParams.endDate = params.endDate;
    } else {
        requestParams.scope = params.scope ?? 5;
    }

    const response = await shippingRequestHttp.service.get<ShipPortReportPage>("/ship/port/report/paging", {
        params: requestParams
    });
    return response.data;
}

/** 统一处理画像字段，确保空值和空字符串沿用 Vue 的默认展示值。 */
function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

/** 将信用码数字转换为旧 Vue 页面使用的红/黄/蓝/绿码文案。 */
function creditCodeText(value: unknown): string {
    if (typeof value === "number") return SHIP_CODE_COLOR.getIntroByCode(value) || DEFAULT_VALUE;
    return text(value);
}

/** 兼容画像列表的字符串、货物对象和港口对象，并按旧页面规则拼接前三项。 */
function listText(value: unknown, field: "goods" | "port"): string {
    if (!Array.isArray(value)) return DEFAULT_VALUE;

    const names = value.slice(0, 3).map(item => {
        if (typeof item === "string") return item;
        if (!item || typeof item !== "object") return "";
        const record = item as Record<string, unknown>;
        const name = field === "goods" ? (record.goodsName ?? record.name) : (record.portName ?? record.name);
        const displayName = text(name);
        return displayName === DEFAULT_VALUE ? "" : displayName;
    });

    return names.filter(Boolean).join("、") || DEFAULT_VALUE;
}

interface ChartPoint {
    label: string;
    value: number;
}

interface ChartSeries {
    label: string;
    values: number[];
}

/** 将 Vue 可能返回的 JSON 字符串或对象集合统一转成数组，避免图表依赖具体序列化形式。 */
function collection(value: unknown): unknown[] {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : collection(parsed);
        } catch {
            return [];
        }
    }
    if (!value || typeof value !== "object") return [];

    const record = value as Record<string, unknown>;
    const nested = record.data ?? record.list ?? record.records ?? record.items;
    return nested === undefined ? Object.values(record) : collection(nested);
}

/** 读取港口或模块对象字段，并把空值安全地转换为可展示文本。 */
function fieldText(value: unknown, keys: string[]): string {
    if (typeof value === "string") return value;
    if (!value || typeof value !== "object") return "";

    const record = value as Record<string, unknown>;
    for (const key of keys) {
        if (record[key] != null && record[key] !== "") return String(record[key]);
    }
    return "";
}

/** 将数字、数字字符串以及 { count/value } 形式的 Vue 数据归一化。 */
function numberValue(value: unknown): number | null {
    const rawValue = value && typeof value === "object" ? fieldValue(value, ["count", "value"]) : value;
    if (rawValue == null || rawValue === "") return null;
    const parsed = typeof rawValue === "number" ? rawValue : Number(rawValue);
    return Number.isFinite(parsed) ? parsed : null;
}

function fieldValue(value: unknown, keys: string[]): unknown {
    if (!value || typeof value !== "object") return undefined;
    const record = value as Record<string, unknown>;
    return keys.map(key => record[key]).find(item => item != null && item !== "");
}

/** 月份标签沿用 Vue 画像的“YYYY-M -> M月”展示规则。 */
function monthText(value: unknown): string {
    const rawValue = fieldText(value, ["label", "name", "month"]) || (typeof value === "string" ? value : "");
    const match = rawValue.match(/^\d{4}-(\d{1,2})$/);
    return match ? `${Number(match[1])}月` : rawValue;
}

/** 港口图表只取接口排序后的前五项，并严格使用 portName 与 num 字段。 */
function portChartData(value: unknown): ChartPoint[] {
    return collection(value)
        .slice(0, 5)
        .map(item => ({
            label: fieldText(item, ["portName"]),
            value: numberValue(fieldValue(item, ["num"])) ?? 0
        }))
        .filter(item => item.label !== "");
}

/** 将模块统计映射到统一折线序列，保持 monthCounts 与时间轴的顺序对应。 */
function lineChartData(value: unknown, monthCount: number): ChartSeries[] {
    return collection(value)
        .map((item, index) => {
            const label = fieldText(item, ["moduleName"]) || `模块${index + 1}`;
            const values = collection(fieldValue(item, ["monthCounts"]))
                .slice(0, monthCount)
                .map(count => numberValue(count) ?? 0);
            return { label, values };
        })
        .filter(item => item.values.length > 0);
}

interface BarChartProps {
    title: string;
    ariaLabel: string;
    data: ChartPoint[];
}

/** 使用固定 viewBox 绘制无尺寸依赖的可访问柱状图，空数据时仍保留图表语义并展示空态。 */
function PortraitBarChart({ title, ariaLabel, data }: BarChartProps) {
    const width = 640;
    const height = 240;
    const left = 48;
    const bottom = 48;
    const plotWidth = width - left - 20;
    const plotHeight = height - bottom - 20;
    const maxValue = Math.max(...data.map(item => item.value), 1);
    const slotWidth = plotWidth / Math.max(data.length, 1);

    return (
        <div className="rounded border border-solid border-gray-200 p-3">
            <Typography.Title className="mb-2 mt-0" level={5}>
                {title}
            </Typography.Title>
            <svg aria-label={ariaLabel} className="h-auto w-full" role="img" viewBox={`0 0 ${width} ${height}`}>
                <line x1={left} x2={width - 20} y1={height - bottom} y2={height - bottom} stroke="#d9d9d9" />
                {data.length > 0 ? (
                    data.map((item, index) => {
                        const barHeight = (item.value / maxValue) * plotHeight;
                        const barWidth = slotWidth * 0.58;
                        const x = left + index * slotWidth + (slotWidth - barWidth) / 2;
                        const y = height - bottom - barHeight;
                        return (
                            <g key={`${item.label}-${index}`}>
                                <rect fill="#1677ff" height={barHeight} rx="2" width={barWidth} x={x} y={y} />
                                <text fill="#595959" textAnchor="middle" x={x + barWidth / 2} y={height - 20}>
                                    {item.label}
                                </text>
                                <text fill="#262626" textAnchor="middle" x={x + barWidth / 2} y={Math.max(y - 6, 14)}>
                                    {item.value}
                                </text>
                            </g>
                        );
                    })
                ) : (
                    <text fill="#8c8c8c" textAnchor="middle" x={width / 2} y={height / 2}>
                        暂无数据
                    </text>
                )}
            </svg>
        </div>
    );
}

interface LineChartProps {
    title: string;
    ariaLabel: string;
    months: string[];
    series: ChartSeries[];
}

/** 使用固定坐标绘制折线图，同时将月份、图例和点值作为 SVG 文本提供给辅助技术。 */
function PortraitLineChart({ title, ariaLabel, months, series }: LineChartProps) {
    const width = 640;
    const height = 280;
    const left = 48;
    const right = 20;
    const top = 56;
    const bottom = 48;
    const plotWidth = width - left - right;
    const plotHeight = height - top - bottom;
    const allValues = series.flatMap(item => item.values);
    const maxValue = Math.max(...allValues, 1);
    const hasData = months.length > 0 && series.length > 0;
    const xAt = (index: number) =>
        months.length <= 1 ? left + plotWidth / 2 : left + (index / (months.length - 1)) * plotWidth;
    const colors = ["#1677ff", "#52c41a", "#fa8c16", "#eb2f96", "#722ed1"];

    return (
        <div className="rounded border border-solid border-gray-200 p-3">
            <Typography.Title className="mb-2 mt-0" level={5}>
                {title}
            </Typography.Title>
            <svg aria-label={ariaLabel} className="h-auto w-full" role="img" viewBox={`0 0 ${width} ${height}`}>
                {hasData ? (
                    <>
                        <line x1={left} x2={left} y1={top} y2={height - bottom} stroke="#d9d9d9" />
                        <line x1={left} x2={width - right} y1={height - bottom} y2={height - bottom} stroke="#d9d9d9" />
                        {series.map((item, seriesIndex) => {
                            const color = colors[seriesIndex % colors.length];
                            const points = months.map((_month, monthIndex) => {
                                const value = item.values[monthIndex] ?? 0;
                                const x = xAt(monthIndex);
                                const y = height - bottom - (value / maxValue) * plotHeight;
                                return { value, x, y };
                            });
                            return (
                                <g key={`${item.label}-${seriesIndex}`}>
                                    <line
                                        stroke={color}
                                        strokeWidth="3"
                                        x1={left + seriesIndex * 120}
                                        x2={left + 12 + seriesIndex * 120}
                                        y1={24}
                                        y2={24}
                                    />
                                    <text fill="#595959" x={left + 18 + seriesIndex * 120} y={28}>
                                        {item.label}
                                    </text>
                                    <polyline
                                        fill="none"
                                        points={points.map(point => `${point.x},${point.y}`).join(" ")}
                                        stroke={color}
                                        strokeWidth="3"
                                    />
                                    {points.map((point, pointIndex) => (
                                        <g key={`${item.label}-${pointIndex}`}>
                                            <circle cx={point.x} cy={point.y} fill={color} r="4" />
                                            <text fill="#262626" textAnchor="middle" x={point.x} y={point.y - 8}>
                                                {point.value}
                                            </text>
                                        </g>
                                    ))}
                                </g>
                            );
                        })}
                        {months.map((month, index) => (
                            <text
                                key={`${month}-${index}`}
                                fill="#595959"
                                textAnchor="middle"
                                x={xAt(index)}
                                y={height - 20}
                            >
                                {month}
                            </text>
                        ))}
                    </>
                ) : (
                    <text fill="#8c8c8c" textAnchor="middle" x={width / 2} y={height / 2}>
                        暂无数据
                    </text>
                )}
            </svg>
        </div>
    );
}

/** 报告时间沿用 Vue 表格展示规则，仅保留日期部分。 */
function reportDateText(value: unknown): string {
    if (value == null || value === "") return DEFAULT_VALUE;
    const dateValue = String(value);
    return dateValue.split("T")[0] || DEFAULT_VALUE;
}

/** 货量字段兼容数字和数字字符串，并沿用旧页面的千分位格式。 */
function tonText(value: unknown): string {
    if (value == null || value === "") return DEFAULT_VALUE;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue.toLocaleString() : DEFAULT_VALUE;
}

interface PortraitValueProps {
    label: string;
    value: string;
}

/** 使用单一文本节点呈现标签和值，保持详情页简洁并便于完整读取字段文案。 */
function PortraitValue({ label, value }: PortraitValueProps) {
    return (
        <div className="rounded border border-solid border-gray-200 px-4 py-3">
            <span>
                {label}：{value}
            </span>
        </div>
    );
}

/** 船舶详情-船舶画像页签，覆盖 Vue 画像核心指标和时间范围查询。 */
export function ShipPortraitPanel({
    shipId,
    shipState,
    loadPortrait = loadShipPortrait,
    loadPortReports: loadPortReportsFetcher = loadPortReports
}: ShipPortraitPanelProps) {
    const [scope, setScope] = useState(5);
    // 保留旧 Vue date-select 的半选状态；只有完整范围才切换为自定义日期查询。
    const [dateRange, setDateRange] = useState<ShipPortraitDateRange>(null);
    const [portrait, setPortrait] = useState<ShipPortraitData | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [portReports, setPortReports] = useState<ShipPortReport[]>([]);
    const [portReportsTotal, setPortReportsTotal] = useState(0);
    const [portReportsQuery, setPortReportsQuery] = useState({ page: 1, size: 10 });
    const [portReportsLoading, setPortReportsLoading] = useState(false);
    const [portReportsErrorMessage, setPortReportsErrorMessage] = useState("");

    // 自定义日期沿用 Vue 的整日边界，且完整范围时不再提交快捷 scope。
    const portraitQuery = useMemo<ShipPortraitQuery>(() => {
        const [startDate, endDate] = dateRange || [];
        if (startDate && endDate) {
            return {
                startDate: `${startDate}T00:00`,
                endDate: `${endDate}T23:59`
            };
        }
        return { scope };
    }, [dateRange, scope]);

    /** 按当前船舶和时间范围加载画像，避免卸载后的异步结果污染页面状态。 */
    useEffect(() => {
        if (!shipId) {
            setPortrait(null);
            setErrorMessage("缺少船舶编号");
            return;
        }

        let disposed = false;
        setErrorMessage("");
        void loadPortrait(shipId, portraitQuery)
            .then(result => {
                if (disposed) return;
                setPortrait(result || {});
            })
            .catch(() => {
                if (disposed) return;
                setPortrait(null);
                setErrorMessage("船舶画像加载失败");
            });

        return () => {
            disposed = true;
        };
    }, [loadPortrait, portraitQuery, shipId]);

    /** 按当前 scope、页码和页大小加载进出港明细，避免旧请求覆盖新查询结果。 */
    useEffect(() => {
        if (!shipId) {
            setPortReports([]);
            setPortReportsTotal(0);
            setPortReportsErrorMessage("缺少船舶编号");
            return;
        }

        let disposed = false;
        setPortReportsLoading(true);
        setPortReportsErrorMessage("");
        void loadPortReportsFetcher(shipId, { ...portReportsQuery, ...portraitQuery })
            .then(result => {
                if (disposed) return;
                setPortReports(result?.list || []);
                setPortReportsTotal(result?.total || 0);
            })
            .catch(() => {
                if (disposed) return;
                setPortReports([]);
                setPortReportsTotal(0);
                setPortReportsErrorMessage("进出港明细加载失败");
            })
            .finally(() => {
                if (!disposed) setPortReportsLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [loadPortReportsFetcher, portraitQuery, portReportsQuery, shipId]);

    /** 点击时间范围时同步回到第一页，由两个 effect 分别刷新画像和进出港明细。 */
    const handleScopeChange = (nextScope: number) => {
        setDateRange(null);
        setScope(nextScope);
        setPortReportsQuery(current => (current.page === 1 ? current : { ...current, page: 1 }));
    };

    /** 完整日期范围才触发自定义查询，半选或清空时回退到当前快捷范围。 */
    const handleDateRangeChange = (_value: unknown, formattedValue: [string, string] | null) => {
        setDateRange(formattedValue);
        setPortReportsQuery(current => (current.page === 1 ? current : { ...current, page: 1 }));
    };

    const certified = Number(shipState) === SHIP_INFO_STATE.CERTIFIED.code;
    const data = portrait || {};
    // 图表数据在渲染前完成归一化，保证接口返回字符串或对象时不依赖浏览器尺寸和序列化细节。
    const timelineMonths = collection(data.timelineMonths).map(monthText).filter(Boolean);
    const visitChartSeries = lineChartData(data.platformVisitModuleStats, timelineMonths.length);
    const businessChartSeries = lineChartData(data.platformBusinessModuleStats, timelineMonths.length);

    // 用 zhihao-ui-react 列契约承接旧 Vue 船舶画像进出港明细表。
    const portReportColumns = useMemo<TableColumnProps<ShipPortReport>[]>(
        () => [
            {
                key: "index",
                label: "序号",
                prop: "index",
                render: ({ rowIndex }) => (portReportsQuery.page - 1) * portReportsQuery.size + rowIndex + 1
            },
            { key: "inOutType", label: "进出港类型", prop: "inOutType", render: ({ row }) => text(row.inOutType) },
            { key: "portName", label: "港口", prop: "portName", render: ({ row }) => text(row.portName) },
            {
                key: "reportTime",
                label: "报告时间",
                prop: "reportTime",
                render: ({ row }) => reportDateText(row.reportTime)
            },
            { key: "goodsName", label: "货物名称", prop: "goodsName", render: ({ row }) => text(row.goodsName) },
            {
                key: "actualTon",
                label: "实载货量（吨）",
                prop: "actualTon",
                render: ({ row }) => tonText(row.actualTon)
            },
            {
                key: "loadingUnloadingTon",
                label: "本港装/卸货量",
                prop: "loadingUnloadingTon",
                render: ({ row }) => tonText(row.loadingUnloadingTon)
            }
        ],
        [portReportsQuery.page, portReportsQuery.size]
    );

    return (
        <section aria-label="ship-portrait-panel" className="flex flex-col gap-4">
            <Card>
                <Space align="center" className="mb-4 w-full" wrap>
                    <Typography.Title className="mb-0 mt-0" level={5}>
                        船舶画像
                    </Typography.Title>
                    <Space wrap>
                        {[
                            { label: "近一个月", value: 1 },
                            { label: "近三个月", value: 2 },
                            { label: "近半年", value: 5 }
                        ].map(option => (
                            <Button
                                key={option.value}
                                aria-pressed={scope === option.value}
                                type={scope === option.value ? "primary" : "default"}
                                onClick={() => handleScopeChange(option.value)}
                            >
                                {option.label}
                            </Button>
                        ))}
                        <ZhDateRangePicker
                            endPlaceholder="画像结束日期"
                            format="YYYY-MM-DD"
                            rangeSeparator="至"
                            startPlaceholder="画像开始日期"
                            type="daterange"
                            value={dateRange}
                            valueFormat="YYYY-MM-DD"
                            onChange={handleDateRangeChange}
                        />
                    </Space>
                </Space>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PortraitValue label="船舶信用码" value={creditCodeText(data.creditCode)} />
                    <PortraitValue label="船舶种类" value={text(data.shipType)} />
                    <PortraitValue label="常运货物" value={listText(data.usualGoodsList, "goods")} />
                    <PortraitValue label="常进港" value={listText(data.usualEntryPorts, "port")} />
                    <PortraitValue label="常出港" value={listText(data.usualExitPorts, "port")} />
                    <PortraitValue label="参考载重吨" value={text(data.referenceTon)} />
                    {certified ? (
                        <>
                            <PortraitValue label="船主平台访问量" value={text(data.platformVisitCount)} />
                            <PortraitValue label="报价数" value={text(data.quoteCount)} />
                            <PortraitValue label="运单数" value={text(data.shippingOrderCount)} />
                            <PortraitValue label="报空船数" value={text(data.emptyShipReportCount)} />
                        </>
                    ) : null}
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PortraitBarChart
                        ariaLabel="常进港柱状图"
                        data={portChartData(data.usualEntryPorts)}
                        title="常进港"
                    />
                    <PortraitBarChart
                        ariaLabel="常出港柱状图"
                        data={portChartData(data.usualExitPorts)}
                        title="常出港"
                    />
                </div>
                {certified ? (
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <PortraitLineChart
                            ariaLabel="船主平台访问量折线图"
                            months={timelineMonths}
                            series={visitChartSeries}
                            title="船主平台访问量趋势"
                        />
                        <PortraitLineChart
                            ariaLabel="船舶平台业务量折线图"
                            months={timelineMonths}
                            series={businessChartSeries}
                            title="船舶平台业务量趋势"
                        />
                    </div>
                ) : null}
            </Card>
            <Card>
                <ZhTable<ShipPortReport>
                    border
                    data={portReports}
                    emptyText={portReportsLoading ? "加载中..." : "暂无进出港记录"}
                    pagination={false}
                    rowKey={row => String(row.id ?? row.reportId ?? "port-report")}
                    columns={portReportColumns}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={5}>
                                进出港明细
                            </Typography.Title>

                            {portReportsErrorMessage ? (
                                <Alert className="mb-4" message={portReportsErrorMessage} role="alert" type="error" />
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 flex justify-end"
                                current={portReportsQuery.page}
                                pageSize={portReportsQuery.size}
                                hideOnSinglePage
                                total={portReportsTotal}
                                onChange={(page, size) => setPortReportsQuery({ page, size })}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default ShipPortraitPanel;
